import { useCallback, useEffect, useRef, useState } from 'react';

import { api, ApiClientError } from '../../services/api';
import type { LinkedDevice } from '../../types';

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return date.toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRelative = (iso: string): string => {
  const diff = Date.now() - Date.parse(iso);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'zojuist';
  if (minutes < 60) return `${minutes} min geleden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} uur geleden`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} dagen geleden`;
  return formatDate(iso);
};

const deviceIcon = (type: string): string => {
  if (type === 'iOS' || type === 'Android' || type === 'Mobile') return '📱';
  if (type === 'Desktop') return '💻';
  return '🌐';
};

const LinkedDevices = () => {
  const [devices, setDevices] = useState<LinkedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmUnlink, setConfirmUnlink] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await api.listDevices();
      setDevices(result.devices);
      setError(null);
    } catch (reason) {
      setError(
        reason instanceof ApiClientError
          ? reason.message
          : 'Kon apparaten niet laden.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const handleUnlink = async (deviceId: string) => {
    setUnlinking(true);
    setError(null);
    try {
      await api.unlinkDevice(deviceId);
      setConfirmUnlink(null);
      await load();
    } catch (reason) {
      setError(
        reason instanceof ApiClientError
          ? reason.message
          : 'Kon apparaat niet ontkoppelen.',
      );
    } finally {
      setUnlinking(false);
    }
  };

  const handleUnlinkAllExcept = async (deviceId: string) => {
    setUnlinking(true);
    setError(null);
    try {
      await api.unlinkAllExcept(deviceId);
      setConfirmUnlink(null);
      await load();
    } catch (reason) {
      setError(
        reason instanceof ApiClientError
          ? reason.message
          : 'Kon apparaten niet ontkoppelen.',
      );
    } finally {
      setUnlinking(false);
    }
  };

  if (loading) {
    return (
      <div className="linked-devices">
        <h3>Gekoppelde apparaten</h3>
        <p className="muted">Laden…</p>
      </div>
    );
  }

  if (error && devices.length === 0) {
    return (
      <div className="linked-devices">
        <h3>Gekoppelde apparaten</h3>
        <p className="notice error">{error}</p>
        <button type="button" className="button secondary small" onClick={() => void load()}>
          Opnieuw laden
        </button>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="linked-devices">
        <h3>Gekoppelde apparaten</h3>
        <p className="muted">Geen apparaten gekoppeld.</p>
      </div>
    );
  }

  return (
    <div className="linked-devices">
      <h3>Gekoppelde apparaten</h3>
      {error && <p className="notice error">{error}</p>}
      <ul className="device-list">
        {devices.map((device) => (
          <li key={device.deviceId} className="device-item">
            <div className="device-info">
              <span className="device-icon" aria-hidden="true">
                {deviceIcon(device.deviceType)}
              </span>
              <div className="device-details">
                <strong>{device.deviceName}</strong>
                {device.isCurrent && <span className="badge current">Dit apparaat</span>}
                <span className="device-type">{device.deviceType}</span>
                <span className="device-meta">
                  Gekoppeld: {formatDate(device.pairedAt)}
                </span>
                <span className="device-meta">
                  Laatst actief: {formatRelative(device.lastActiveAt)}
                </span>
              </div>
            </div>
            <div className="device-actions">
              {confirmUnlink === device.deviceId ? (
                <>
                  <span className="muted small">Weet je het zeker?</span>
                  <button
                    type="button"
                    className="button danger small"
                    disabled={unlinking}
                    onClick={() => void handleUnlink(device.deviceId)}
                  >
                    Ja, ontkoppelen
                  </button>
                  <button
                    type="button"
                    className="button secondary small"
                    disabled={unlinking}
                    onClick={() => setConfirmUnlink(null)}
                  >
                    Annuleren
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="button danger-outline small"
                  onClick={() => setConfirmUnlink(device.deviceId)}
                >
                  {device.isCurrent ? 'Dit apparaat ontkoppelen' : 'Ontkoppelen'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {devices.length > 1 && (
        <div className="unlink-all-section">
          <button
            type="button"
            className="button danger-outline small"
            onClick={() => {
              const current = devices.find((d) => d.isCurrent);
              if (current) void handleUnlinkAllExcept(current.deviceId);
            }}
          >
            Alle andere apparaten ontkoppelen
          </button>
        </div>
      )}
    </div>
  );
};

export default LinkedDevices;
